<template>
  <div
    class="task-session card"
    @click="handleSessionInfoClick(session)"
  >
    <div class="statistics">

      <div class="task-session__header">
        <div class="left">
          <div class="icon-container">
            <!-- Status tag -->
            <span v-if="sessionStatus?.toLowerCase().includes('complete')" class="status-tag _complete"><div class="status-icon">&#x2713;</div>Complete</span>
            <span v-else-if="sessionStatus?.toLowerCase().includes('error')" class="status-tag _error"><div class="status-icon">&#x2716;</div>Error</span>
            <span v-else-if="sessionStatus?.toLowerCase().includes('terminate')" class="status-tag _warning"><div class="status-icon">&#x21;</div>Terminated</span>
            <span v-else-if="sessionStatus" class="status-tag _running"><div class="running-icon" />
              {{ String(sessionStatus).charAt(0).toUpperCase() + String(sessionStatus).slice(1) }}
            </span>
            <!-- Manage type icons -->
            <v-icon
              v-if="isCloud"
              v-tooltip="'Cloud Hosted Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-cloud
            </v-icon>
            <v-icon
              v-if="isUserManaged"
              v-tooltip="'User Managed Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-laptop
            </v-icon>
            <v-icon
              v-if="isKubernetes"
              v-tooltip="'Kubernetes Managed Task'"
              size="20"
              color="#666"
              style="margin-left: 12px;"
            >
              mdi-kubernetes
            </v-icon>
          </div>
        </div>
        <div class="right">
          <div class="icon-container">
            <IconButton
              v-tooltip="`Refresh task`"
              class="icon refresh-icon"
              :icon="'refresh'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="handleRefresh()"
            />
            <IconButton
              v-tooltip="`Clone Task`"
              class="icon maximize-icon"
              :icon="'copy'"
              :width="20"
              :height="20"
              :type="'click'"
              @click.stop="cloneTask(session)"
            />
            <IconButton
              v-tooltip="`View charts for task`"
              class="icon analytics-icon"
              :icon="'analytics'"
              :width="22"
              :height="22"
              :type="'click'"
              @click.stop="handleShowChartsClick()"
            />
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Terminate task. This will stop the task, but you can still view artifacts`"
              class="icon stop-icon"
              :icon="'stop_circle'"
              :width="22"
              :height="22"
              :type="'click'"
              @click.stop="terminateTask(session)"
            />
            <IconButton
              v-if="permissions && permissions.trainer_write"
              v-tooltip="`Move task to recycle bin`"
              class="icon delete delete-icon"
              :icon="'trash'"
              :width="22"
              :height="22"
              :type="'click'"
              @click.stop="deleteTask()"
            />
          </div>
        </div>
      </div>

      
    <div class="body">
        <div class="body__header">
          <div class="owner-id-display">
            <UserIcon
              :user="userList?.find((e) => e.username === session.username)"
              :size="'32px'"
            />
            <EncodedIdField
              :id="session.id"
              :type="'task'"
              :hideLabel="false"
            />

            <!-- Error/Terminated cross icon -->
            <div 
              v-if="sessionStatus?.toLowerCase().includes('error') || sessionStatus?.toLowerCase().includes('terminate')" 
              class="circular-progress-container"
            >
              <div class="error-cross-icon">
                <svg width="55" height="55" viewBox="0 0 55 55">
                  <line x1="15" y1="15" x2="40" y2="40" stroke="#d32f2f" stroke-width="3" stroke-linecap="butt"/>
                  <line x1="40" y1="15" x2="15" y2="40" stroke="#d32f2f" stroke-width="3" stroke-linecap="butt"/>
                </svg>
              </div>
            </div>
            <!-- Circular Progress where status was -->
            <div v-else-if="!sessionStatus?.toLowerCase().includes('complete') && session.status_info?.stage_label" class="circular-progress-container">
              <div class="stage-label-left">{{ session.status_info.stage_label }}</div>
              <div class="circular-progress">
                <svg width="55" height="55" viewBox="0 0 55 55">
                  <circle
                    cx="27.5"
                    cy="27.5"
                    r="22"
                    fill="none"
                    stroke="#f0f0f0"
                    stroke-width="5"
                  />
                  <circle
                    cx="27.5"
                    cy="27.5"
                    r="22"
                    fill="none"
                    stroke="#2e7d32"
                    stroke-width="5"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="progressOffset"
                    stroke-linecap="butt"
                    transform="rotate(-90 27.5 27.5)"
                  />
                </svg>
                <div class="percentage-text">{{ Math.round(session.status_info?.percent || 0) }}%</div>
                <div class="progress-divider"></div>
                <div 
                  v-if="session.status_info?.stage_number && session.status_info?.total_stages" 
                  class="stage-counter-text"
                >
                  {{ session.status_info.stage_number }} / {{ session.status_info.total_stages }}
                </div>
              </div>
            </div>
            <!-- Waiting animation for init/queued status with no stage label -->
            <div 
              v-else-if="(sessionStatus?.toLowerCase().includes('init') || sessionStatus?.toLowerCase().includes('queued')) && !session.status_info?.stage_label" 
              class="circular-progress-container"
            >
              <div class="circular-progress rotating">
                <svg width="55" height="55" viewBox="0 0 55 55">
                  <circle
                    cx="27.5"
                    cy="27.5"
                    r="22"
                    fill="none"
                    stroke="#f0f0f0"
                    stroke-width="5"
                  />
                  <circle
                    cx="27.5"
                    cy="27.5"
                    r="22"
                    fill="none"
                    stroke="#1565C0"
                    stroke-width="5"
                    :stroke-dasharray="circumference * 0.25 + ' ' + circumference * 0.75"
                    stroke-linecap="butt"
                    transform="rotate(-90 27.5 27.5)"
                  />
                </svg>
                <div class="percentage-text">...</div>
              </div>
            </div>
          </div>

          <div class="message-display">
            <div 
              v-if="(sessionStatus?.toLowerCase().includes('init') || sessionStatus?.toLowerCase().includes('queued')) && !session.status_info?.stage_label"
              class="message-value message-waiting"
            >
              Waiting...
            </div>
            <div 
              v-else-if="(sessionStatus?.toLowerCase().includes('error') || sessionStatus?.toLowerCase().includes('terminate')) && session.message"
              class="message-value message-error"
            >
              {{ session.message }}
            </div>
            <div 
              v-else-if="session.status_info?.message"
              class="message-value message-success"
            >
              {{ session.status_info.message }}
            </div>
          </div>

        </div>
     
        <hr>
        <div class="body__grid scrollbar">
          <div class="statistics-field">
            <div class="description">Name</div>
            <div 
              v-tooltip="session && session.name ? session.name : 'Unknown'"
              class="value task-name-value"
            >
              {{ session && session.name ? session.name : "Unknown" }}
            </div>
          </div>
          <div v-if="session.type" class="statistics-field">
            <div class="description">Type</div>
            <div class="value">{{ session.type }}</div>
          </div>
          <div v-if="session.parent && session.parent.id" class="statistics-field">
            <div class="description">Activity</div>
            <div class="value">
              <span 
                v-if="(session.type === 'trainer' || session.type === 'validation') && session.parent.uid"
                v-tooltip="session.parent.name || session.parent.uid"
                class="hyperlink"
                style="cursor: pointer;"
                @click="handleNavigateToActivity()"
              >
                {{ session.parent.uid }}
              </span>
              <span 
                v-else-if="session.parent.name"
                v-tooltip="session.parent.name"
                class="hyperlink"
                style="cursor: pointer;"
                @click="handleNavigateToActivity()"
              >
                {{ session.parent.name }}
              </span>
              <span v-else-if="session.parent.id" style="font-size: 0.85rem; color: #999;">
                ID: {{ session.parent.id }}
              </span>
            </div>
          </div>
          <div v-if="session.type === 'trainer' && session.parsed_data?.training_session?.id" class="statistics-field">
            <div class="description">Training Session</div>
            <div class="value">
              <span 
                v-if="session.parsed_data.training_session.name"
                v-tooltip="session.parsed_data.training_session.name"
                class="hyperlink"
                style="cursor: pointer;"
                @click="handleNavigateToTrainingSession()"
              >
                {{ session.parsed_data.training_session.name }}
              </span>
            </div>
          </div>
          <div class="statistics-field">
            <div class="description">Start Date</div>
            <div class="value"><span v-if="session">{{ formatTime(session.date) }}</span></div>
          </div>
          <div class="statistics-field">
            <div class="description">Duration</div>
            <div class="value"><span v-if="session && getEndTime(session)" class="duration">{{ getEndTime(session) }}</span></div>
          </div>
        </div> 

        <!-- Process ID -->
        <div class="process-id-container">
          <span class="process-id-label">Process ID:</span>
          <span 
            v-tooltip="session.cloud_instance_id"
            class="process-id-value"
          >
            {{ session.cloud_instance_id }}
          </span>
          <IconButton
            v-tooltip="'Copy Process ID'"
            class="icon copy-icon"
            :icon="'copy'"
            :width="14"
            :height="14"
            :type="'click'"
            @click.stop="copyProcessId"
          />
        </div>

      </div> 




    </div>



    
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import IconButton from '@/components/IconButton.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import EncodedIdField from '@/components/EncodedIdField.vue';
import TaskStatusBars from '@/components/TaskStatusBars.vue';
import UserIcon from '@/components/UserIcon.vue';

const props = defineProps({
  session: {
    type: Object,
    default: () => {},
  },
  permissions: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['info-clicked', 'delete-session', 'refresh-session', 'show-charts', 'select-session', 'terminate-session', 'clone-task']);

const $toast = useToast();
const store = useStore();
const router = useRouter();

const dsConn = new DatastoreConnect(store.state.enterpriseServerUrl);
const show_menu = ref(false);
const hideProgress = ref(false);
const sessionStatus = ref("");

const showProgress = computed(() => {
  const dt = props.session.docker_task;
  if (dt.status?.toLowerCase().includes('complete')) return false;
  if (dt.data.length < 4) return false;
  return true;
});

const userList = computed(() => store.state.user.userList);

// Circular progress calculations
const circumference = computed(() => 2 * Math.PI * 22); // radius is 22
const progressOffset = computed(() => {
  const percent = props.session.status_info?.percent || 0;
  return circumference.value - (percent / 100) * circumference.value;
});

// Manage type helpers
const isCloud = computed(() => {
  return props.session?.manage_type === '' || props.session?.manage_type?.includes('aws');
});
const isUserManaged = computed(() => {
  return props.session?.manage_type === 'user';
});
const isKubernetes = computed(() => {
  return props.session?.manage_type === 'kubernetes';
});

watch(
  () => props.session,
  (newVal) => {
    if (newVal) {
      getStatus(newVal);
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.session) {
    getStatus(props.session);
  }
});


const handleSessionInfoClick = () => {
  emit('info-clicked');
};

const getStatus = (session) => {
  sessionStatus.value = session.status;
};

const handleNavigateToActivity = () => {
  if (props.session?.type === 'trainer' && props.session?.parent?.id) {
    // For trainer tasks, parent is the training session
    router.push({ name: 'experiment.training.details', query: { train_session_id: props.session.parent.id } });
  } else if (props.session?.type === 'validation' && props.session?.parent?.id) {
    // For validation tasks, parent is the validation session
    router.push({ name: 'experiment.validation.details', query: { val_session_id: props.session.parent.id } });
  } else if (props.session?.parent?.id) {
    // For other tasks, go to apps
    router.push({ name: 'apps', query: { activity_id: props.session.parent.id } });
  }
};

const handleNavigateToTrainingSession = () => {
  if (props.session?.parsed_data?.training_session?.id) {
    router.push({ name: 'experiment.training.details', query: { train_session_id: props.session.parsed_data.training_session.id } });
  }
};

const handleNavigateToValidationSession = () => {
  if (props.session?.type === 'validation' && props.session?.parent?.id) {
    // For validation tasks, parent is the validation session
    router.push({ name: 'experiment.validation.details', query: { val_session_id: props.session.parent.id } });
  } else if (props.session?.parsed_data?.validation_session?.id) {
    router.push({ name: 'experiment.validation.details', query: { val_session_id: props.session.parsed_data.validation_session.id } });
  }
};


const getEndTime = (docker_task) => {
  if (docker_task.end_date !== "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = new Date(docker_task.end_date) - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else if (docker_task.end_date === "0001-01-01T00:00:00Z" && docker_task.date !== "0001-01-01T00:00:00Z") {
    const msDiff = Date.now() - new Date(docker_task.date);
    const minutes = Math.floor(msDiff / (1000 * 60)) % 60;
    const hours = Math.floor(msDiff / (1000 * 60 * 60)) % 24;
    const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
    const result = [];
    if (days > 0) result.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) result.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) result.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    return result.join(" ") || "0 mins";
  } else {
    return null;
  }
};


const formatTime = (str) => {
  return `${new Date(str).toLocaleTimeString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })}`;
};

const deleteTask = async () => {
  try {
    const params = {
      docker_task_id: props.session.id
    };
    const response = await dsConn.deleteDockerTask(params);
    if (response && response.result) {
      emit('delete-session', props.session);
    }
  } catch (error) {
    console.error('Error deleting task:', error);
  }
};

const cloneTask = (session) => {
  emit('clone-task', session);
};

const handleRefresh = async () => {
  try {
    const params = {
      id: props.session.id
    };
    const response = await dsConn.api("job.list", params);
    if (response && response.result) {
      // Optionally update the session data or emit to parent
      emit('refresh-session', response.result);
    }
  } catch (error) {
    console.error('Error refreshing task:', error);
  }
};

const handleShowChartsClick = () => {
  emit('show-charts', props.session);
};

const terminateTask = async (session) => {
  try {
    const params = {
      task_id: session.id
    };
    const response = await dsConn.api("job.stop", params);
    if (response && response.result) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: 'Termination request submitted',
        type: 'success',
        position: 'top'
      });
      emit('terminate-session', session);
    }
    if (response && response.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: response.error.message || 'Failed to terminate task',
        type: 'error',
        position: 'top'
      });
    }
  } catch (error) {
    console.error('Error terminating task:', error);
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Failed to terminate task',
      type: 'error',
      position: 'top'
    });
  }
};

const copyProcessId = async () => {
  if (props.session.cloud_instance_id) {
    try {
      await navigator.clipboard.writeText(props.session.cloud_instance_id);
    } catch (error) {
      console.error('Failed to copy process ID:', error);
    }
  }
};

const navigateToTrainingSession = (id) => {
  // Note: router needs to be accessed via useRouter() in composition API
  // import { useRouter } from 'vue-router';
  // const router = useRouter();
  // router.push({ name: 'experiment.training.details', query: { train_session_id: id } });
};
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  position: relative;
  height: fit-content;
  margin: 0;
  text-align: left;
  border-radius: 8px;
  box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 0.05);
}

.task-session {
  display: flex;
  flex-direction: row;
  border-radius: 8px;
  height: 280px;
  width: 350px;
  padding: 0;
  cursor: pointer;

  &__header {
    display: flex;
    flex-direction: row;
    padding: 4px 10px 4px 0px;
    background: var(--gray-200);
    height: 32px;
  }

  .progress {
    display: flex;
    flex-direction: column;
    height: 100%;
    //width: 40%;
    width: 350px;
  }
  .statistics {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }

  .left {
      width: 50%;
      min-width: 50%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }

  .task-type {
    font-size: 12px;
    font-weight: 500;
    color: var(--body-text-color);
    padding: 2px 6px;
    background: var(--gray-100);
    border-radius: 4px;
  }

  .right {
    width: 50%;
    min-width: 50%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end
  }
}

.section-title {
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 1rem;
  font-weight: 700;
  color: var(--body-text-color-heading);
  letter-spacing: 0.025em;
  gap: 0px;
}

.item-selector{
  width: fit-content;
  height: fit-content;
}

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  max-width: 100%;
}

.body {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  padding: 10px;
  overflow: hidden;

  &__header{
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .owner-id-display {
      display: flex;
      flex-direction: row;
      gap: 10px;
      align-items: flex-start;
      width: 100%;
      min-height: 55px;

      .labels-container {
        flex: 1
      }
    }

    .task-name-value {
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    }

    .message-display {
      margin-top: 4px;
      min-height: 1.1rem;
    }

    .message-value {
      font-size: 0.85rem;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }

    .name-display {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start
    }

    .description {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--body-text-color);
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__grid-field {
    display: block;
    max-width: 300px;
  }

  &__grid-field + &__grid-field {
    margin-top: 10px;
  }

  &__grid {
    display: block;

    .statistics-field {
      display: flex;
      flex-direction: row;
      gap: 10px;
      margin-top: 1px;
    }

    .description {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--body-text-color);
      width: 120px;
      min-width: 120px;
    }
    .value {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--body-text-color-secondary);
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &._separator::after {
    content: "";
    position: absolute;
    right: 0;
    top: 5%;
    bottom: 5%;
    width: 1px;
    background-color: #9a9ca2;
  }
}

.icon-container {
  display: flex;
  align-items: center;
  margin-left: 4px;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
  .icon + .icon {
    margin-left: 14px;
  }
}

.footer {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: -moz-fit-content;
  height: fit-content;
}

.icon {
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.icon:hover {
  @include themify() {
    color: themed('color-primary');
    background: themed('icon-hover-color');
    border-radius: 100%;
    box-shadow: 0 0 0 4px themed('icon-hover-color');
  }
}

.icon.delete:hover {
  color: var(--color-error)
}

.status-tag {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  min-width: 100px;
  border-radius: 18px;
  padding: 4px 6px;
  line-height: 1;
  font-size: 0.625rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid;
  box-shadow: 0px 0px 1px rgb(0 0 0 / 25%), 0px 1px 4px rgb(0 0 0 / 25%);
  color: #4a4a4a;

  &._complete {
    @include themify() {
      background: themed('color-status-complete-background');
      border-color: themed('color-status-complete');
    }
  }
  &._running {
    @include themify() {
      background: themed('color-status-running-background');
      border-color: themed('color-status-running');
    }
  }
  &._error {
    @include themify() {
      background: themed('color-status-error-background');
      border-color: themed('color-status-error');
    }
  }

  &._warning {
    @include themify() {
      background: themed('color-status-warning-background');
      border-color: themed('color-status-warning');
    }
  }

  .running-icon {
    color: inherit;
    width: 4px;
    height: 4px;
    border: solid 4px;
    border-radius: 4px;
    margin-right: 4px;
  }

  .status-icon {
    color: inherit;
    margin-right: 4px;
  }
}

.session-id-field {
  margin-right: 20px;
}

:deep() {
  .v-expansion-panel__shadow {
    box-shadow: none !important;
  }
}

.task-sessions-empty {
  display: flex;
  flex-direction: column;
  height: 150px;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  border: solid 1px black;
  border-radius: 8px;
  @include themify() {
    background: radial-gradient(circle, rgba($color-primary-300, 0.075) 50%, rgba(var(--color-primary-rgb), 0.125) 100%);
  }

  span {
    padding: 16px;
    text-align: center;
  }
}

.select-checkbox{
  position: relative !important;
  top: -7px;
  left: -5px;
}

.process-id-container {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 0;
  margin-top: auto;
  font-size: 9px;
  color: var(--body-text-color-secondary, #888);
  border-top: 1px solid var(--gray-200);
  padding-top: 6px;
}

.process-id-label {
  flex-shrink: 0;
  font-weight: 500;
}

.process-id-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.copy-icon {
  flex-shrink: 0;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
}

.circular-progress-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  min-height: 55px;
}

.stage-label-left {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--body-text-color);
  text-align: right;
  max-width: 80px;
  line-height: 1.2;
  word-wrap: break-word;
}

.error-cross-icon {
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circular-progress {
  position: relative;
  width: 55px;
  height: 55px;
  
  svg {
    transform: rotate(0deg);
    
    circle {
      transition: stroke-dashoffset 0.3s ease;
    }
  }
  
  &.rotating svg {
    animation: rotate-progress 2s linear infinite;
  }
}

@keyframes rotate-progress {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.percentage-text {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.7rem;
  font-weight: 500;
  color: #000000;
}

.progress-divider {
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 1px;
  background-color: #d0d0d0;
}

.stage-counter-text {
  position: absolute;
  top: 65%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.55rem;
  font-weight: 600;
  color: #000000;
  white-space: nowrap;
}

.message-field {
  grid-column: 1 / -1;
  
  .value {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
}

.message-error {
  color: #d32f2f !important;
}

.message-success {
  color: #388e3c !important;
}

.message-waiting {
  color: #0d57ab !important;
}

</style>
